/**
 * One-shot location capture for clock events (§14.4): the browser is asked for a single
 * high-accuracy fix at the moment of clocking in or out — never watched continuously.
 */

export interface GpsLocation {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
}

export type GpsFailure = 'PERMISSION_DENIED' | 'UNAVAILABLE' | 'TIMEOUT';

export type GpsResult = { ok: true; location: GpsLocation } | { ok: false; error: GpsFailure };

export const GPS_ERROR_MESSAGES: Record<GpsFailure, string> = {
  PERMISSION_DENIED:
    'Location access is blocked for this site. Allow location in your browser settings to verify on-site attendance, or continue — your attendance will be flagged for review.',
  UNAVAILABLE: 'Your device could not determine its location. Move near a window or enable location services, or continue and your attendance will be reviewed.',
  TIMEOUT: 'Getting your location took too long. Try again, or continue and your attendance will be reviewed.',
};

export function getCurrentPosition(timeoutMs = 15_000): Promise<GpsResult> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    return Promise.resolve({ ok: false, error: 'UNAVAILABLE' });
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          ok: true,
          location: {
            latitude: Number(pos.coords.latitude.toFixed(6)),
            longitude: Number(pos.coords.longitude.toFixed(6)),
            accuracyMeters: Number.isFinite(pos.coords.accuracy) ? Math.round(pos.coords.accuracy * 10) / 10 : null,
          },
        }),
      (err) => resolve({ ok: false, error: err.code === err.PERMISSION_DENIED ? 'PERMISSION_DENIED' : err.code === err.TIMEOUT ? 'TIMEOUT' : 'UNAVAILABLE' }),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}
